import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Article, Category, Tag, Author, ArticleFormData } from '@/types/article';
import { useToast } from '@/hooks/use-toast';

export function useArticles() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Fetch published articles for public use - using secure function
  const fetchPublishedArticles = async (categorySlug?: string, tagSlug?: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_articles_with_safe_authors', {
        filter_status: 'published',
        filter_category_slug: categorySlug || null,
        filter_tag_slug: tagSlug || null
      });

      if (error) throw error;

      // Transform the flat structure to match Article type
      const formattedArticles = data?.map((article: any) => ({
        ...article,
        category: article.category_id ? {
          id: article.category_id,
          name: article.category_name,
          slug: article.category_slug,
          created_at: '',
          updated_at: '',
          description: null,
          meta_description: null,
          parent_id: null
        } : null,
        author: article.author_id ? {
          id: article.author_id,
          display_name: article.author_display_name,
          bio: article.author_bio,
          avatar_url: article.author_avatar_url,
          social_links: article.author_social_links,
          created_at: '',
          updated_at: '',
          user_id: null
        } : null,
        tags: Array.isArray(article.tags) ? article.tags : []
      })) || [];

      setArticles(formattedArticles as unknown as Article[]);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        title: "Error",
        description: "Failed to fetch articles",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch single article by slug - using secure function
  const fetchArticleBySlug = async (slug: string): Promise<Article | null> => {
    try {
      const { data, error } = await supabase.rpc('get_article_by_slug_safe', {
        article_slug: slug
      });

      if (error) throw error;

      if (data && data.length > 0) {
        const article = data[0];
        
        // Transform the flat structure to match Article type
        return {
          ...article,
          category: article.category_id ? {
            id: article.category_id,
            name: article.category_name,
            slug: article.category_slug,
            created_at: '',
            updated_at: '',
            description: null,
            meta_description: null,
            parent_id: null
          } : null,
          author: article.author_id ? {
            id: article.author_id,
            display_name: article.author_display_name,
            bio: article.author_bio,
            avatar_url: article.author_avatar_url,
            social_links: article.author_social_links,
            created_at: '',
            updated_at: '',
            user_id: null
          } : null,
          tags: Array.isArray(article.tags) ? article.tags : []
        } as unknown as Article;
      }

      return null;
    } catch (error) {
      console.error('Error fetching article:', error);
      return null;
    }
  };

  // Fetch all articles for admin
  const fetchAllArticles = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          category:categories(*),
          author:authors(*),
          tags:article_tags(tag:tags(*))
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedArticles = data?.map(article => ({
        ...article,
        tags: article.tags?.map((t: any) => t.tag) || []
      })) || [];

      setArticles(formattedArticles as Article[]);
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast({
        title: "Error",
        description: "Failed to fetch articles",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name');

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch tags
  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');

      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch authors using secure function (excludes user_id)
  const fetchAuthors = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_author_profiles', { include_user_id: false });
      
      if (error) throw error;
      setAuthors((data || []) as Author[]);
      return data; // Return the data for immediate use
    } catch (error) {
      console.error('Error fetching authors:', error);
      return [];
    }
  };

  // Create article
  const createArticle = async (articleData: ArticleFormData) => {
    setLoading(true);
    try {
      // Use provided author_id or try to auto-assign based on logged-in user
      let finalAuthorId = articleData.author_id;
      
      if (!finalAuthorId) {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Find the author record for the current user
          const { data: authorData, error: authorError } = await supabase
            .from('authors')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (authorError) {
            console.error('Error fetching author:', authorError);
          } else if (authorData) {
            finalAuthorId = authorData.id;
            console.log('Auto-assigned author:', authorData.id);
          } else {
            // User doesn't have an author record linked - this is a warning, not an error
            console.warn('No author profile linked to user account');
            toast({
              title: "Warning",
              description: "Please select an author for this article.",
              variant: "destructive"
            });
          }
        }
      }
      
      // Require author to be selected
      if (!finalAuthorId) {
        toast({
          title: "Error",
          description: "Please select an author before creating the article.",
          variant: "destructive"
        });
        throw new Error('Author is required');
      }
      
      // Generate slug from title
      const slug = articleData.title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');

      let contentWithLinks = articleData.content;
      
      // Auto-link keywords to other articles if publishing or draft (to preview links)
      if (articleData.status === 'published' || articleData.status === 'draft') {
        try {
          console.log('🔗 Calling link-article-keywords function...');
          const { data: linkData, error: linkError } = await supabase.functions.invoke('link-article-keywords', {
            body: { content: articleData.content, currentArticleSlug: slug }
          });
          
          if (linkError) {
            console.error('Link function error:', linkError);
          }
          
          if (linkData?.success && linkData?.content) {
            console.log(`✅ Successfully linked ${linkData.linksAdded || 0} keywords`);
            contentWithLinks = linkData.content;
          } else {
            console.warn('Link function did not return linked content:', linkData);
          }
        } catch (error) {
          console.error('Failed to auto-link keywords:', error);
          // Continue with original content if linking fails
        }
      }

      // Extract tag_ids from articleData before inserting
      const { tag_ids, ...articleDataWithoutTags } = articleData;

      const { data, error } = await supabase
        .from('articles')
        .insert([{
          ...articleDataWithoutTags,
          author_id: finalAuthorId, // Use the auto-assigned or provided author_id
          content: contentWithLinks,
          slug,
          published_at: articleData.status === 'published' ? new Date().toISOString() : null
        }])
        .select()
        .single();

      if (error) throw error;

      // Add tags
      if (articleData.tag_ids && articleData.tag_ids.length > 0) {
        const tagInserts = articleData.tag_ids.map(tagId => ({
          article_id: data.id,
          tag_id: tagId
        }));

        await supabase
          .from('article_tags')
          .insert(tagInserts);
      }

      toast({
        title: "Success",
        description: `Article created successfully${contentWithLinks !== articleData.content ? ' with internal links' : ''}`
      });

      return data;
    } catch (error) {
      console.error('Error creating article:', error);
      toast({
        title: "Error",
        description: "Failed to create article",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update article
  const updateArticle = async (id: string, articleData: ArticleFormData) => {
    setLoading(true);
    try {
      // Validate author is selected for published articles
      if (articleData.status === 'published' && !articleData.author_id) {
        toast({
          title: "Error",
          description: "Please select an author before publishing this article.",
          variant: "destructive"
        });
        throw new Error('Author is required for published articles');
      }
      
      // Get the existing article
      const { data: existingArticle } = await supabase
        .from('articles')
        .select('slug, status')
        .eq('id', id)
        .single();

      // Check if we're editing a published article
      // If so, we should preserve the published status (keep it published)
      const wasPublished = existingArticle?.status === 'published';
      let finalStatus = articleData.status;
      
      // If article was published and user is just editing (status is still published),
      // keep it published. The draft versioning happens automatically via auto-save
      if (wasPublished && articleData.status === 'published') {
        finalStatus = 'published';
      }

      // Generate clean slug when publishing
      let finalSlug = existingArticle?.slug;
      let cleanCanonicalUrl = articleData.canonical_url;
      
      if (finalStatus === 'published') {
        // Always generate clean slug for published articles (remove draft timestamps)
        finalSlug = articleData.title
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .slice(0, 100);
        
        // Auto-generate canonical URL if not provided or if it contains "draft"
        if (!cleanCanonicalUrl || cleanCanonicalUrl.includes('draft')) {
          cleanCanonicalUrl = `https://storymarketing.group/article/${finalSlug}`;
        }
      }

      let contentWithLinks = articleData.content;
      
      // Auto-link keywords to other articles if publishing or draft (to preview links)
      if (finalStatus === 'published' || finalStatus === 'draft') {
        try {
          console.log('🔗 Calling link-article-keywords function...');
          const { data: linkData, error: linkError } = await supabase.functions.invoke('link-article-keywords', {
            body: { 
              content: articleData.content,
              currentArticleSlug: finalSlug 
            }
          });
          
          if (linkError) {
            console.error('Link function error:', linkError);
          }
          
          if (linkData?.success && linkData?.content) {
            console.log(`✅ Successfully linked ${linkData.linksAdded || 0} keywords`);
            contentWithLinks = linkData.content;
          } else {
            console.warn('Link function did not return linked content:', linkData);
          }
        } catch (error) {
          console.error('Failed to auto-link keywords:', error);
          // Continue with original content if linking fails
        }
      }

      // Extract tag_ids from articleData before updating
      const { tag_ids, ...articleDataWithoutTags } = articleData;

      // Clean up timestamps: convert empty strings to null
      const cleanScheduledAt = articleData.scheduled_at?.trim() ? articleData.scheduled_at : null;
      
      // Preserve published_at if article was already published
      let publishedAt = existingArticle?.status === 'published' 
        ? undefined // Don't update published_at if already published
        : (finalStatus === 'published' ? new Date().toISOString() : null);

      const { data, error } = await supabase
        .from('articles')
        .update({
          ...articleDataWithoutTags,
          slug: finalSlug,
          canonical_url: cleanCanonicalUrl,
          content: contentWithLinks,
          status: finalStatus,
          scheduled_at: cleanScheduledAt,
          ...(publishedAt !== undefined && { published_at: publishedAt })
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Create revision snapshot before update
      if (existingArticle) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // Get current version number
            const { data: latestRevision } = await supabase
              .from('article_revisions')
              .select('version_number')
              .eq('article_id', id)
              .order('version_number', { ascending: false })
              .limit(1)
              .single();

            const nextVersion = (latestRevision?.version_number || 0) + 1;

            // Create revision with old content
            const { data: currentArticle } = await supabase
              .from('articles')
              .select('title, content')
              .eq('id', id)
              .single();

            if (currentArticle) {
              await supabase
                .from('article_revisions')
                .insert({
                  article_id: id,
                  title: currentArticle.title,
                  content: currentArticle.content,
                  version_number: nextVersion,
                  created_by: user.id
                });
              
              console.log(`✅ Created revision version ${nextVersion} before update`);
            }
          }
        } catch (revisionError) {
          console.error('Failed to create revision:', revisionError);
          // Continue with update even if revision fails
        }
      }

      // Update tags
      console.log('🏷️ Updating tags for article:', id);
      console.log('🏷️ Tag IDs to insert:', articleData.tag_ids);
      
      const { error: deleteTagsError } = await supabase
        .from('article_tags')
        .delete()
        .eq('article_id', id);
      
      if (deleteTagsError) {
        console.error('❌ Error deleting old tags:', deleteTagsError);
        throw new Error(`Failed to delete old tags: ${deleteTagsError.message}`);
      }
      
      console.log('✅ Deleted old tags');

      if (articleData.tag_ids && articleData.tag_ids.length > 0) {
        const tagInserts = articleData.tag_ids.map(tagId => ({
          article_id: id,
          tag_id: tagId
        }));

        console.log('🏷️ Inserting tags:', tagInserts);
        
        const { error: insertTagsError } = await supabase
          .from('article_tags')
          .insert(tagInserts);
        
        if (insertTagsError) {
          console.error('❌ Error inserting tags:', insertTagsError);
          throw new Error(`Failed to insert tags: ${insertTagsError.message}`);
        }
        
        console.log('✅ Successfully inserted', tagInserts.length, 'tags');
      }

      toast({
        title: "Success",
        description: `Article updated successfully${contentWithLinks !== articleData.content ? ' with internal links' : ''}`
      });

      return data;
    } catch (error) {
      console.error('Error updating article:', error);
      toast({
        title: "Error",
        description: "Failed to update article",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete article
  const deleteArticle = async (id: string) => {
    try {
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Article deleted successfully"
      });
    } catch (error) {
      console.error('Error deleting article:', error);
      toast({
        title: "Error",
        description: "Failed to delete article",
        variant: "destructive"
      });
      throw error;
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchTags();
    fetchAuthors();
  }, []);

  return {
    articles,
    categories,
    tags,
    authors,
    loading,
    fetchPublishedArticles,
    fetchArticleBySlug,
    fetchAllArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    fetchCategories,
    fetchTags,
    fetchAuthors
  };
}